import React from 'react';
import styled from 'styled-components';
import { classic } from '~/themes';

type IProps = {
  color: 'primary' | 'secondary';
  textColor?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  hidden?: boolean;
  type?: 'button' | 'submit' | 'reset';
  opaque?: boolean;
  width?: string;
  minWidth?: string;
  minHeight?: string;
  ref?: any;
  margin?: string;
  text?: string;
  secondaryText?: string;
};

export default function ButtonExtended({
  color,
  textColor,
  onClick,
  disabled,
  minHeight,
  hidden,
  type,
  opaque,
  width,
  minWidth,
  margin,
  ref,
  text,
  secondaryText,
}: IProps) {
  return (
    <Container
      color={color}
      disabled={disabled}
      hidden={hidden}
      $minWidth={minWidth}
      onClick={onClick}
      opaque={opaque}
      ref={ref}
      type={type}
      width={width}
      $margin={margin}
      $minHeight={minHeight}
    >
      <SSpanTitle $textColor={textColor}>{text}</SSpanTitle>

      {secondaryText && <SSpan>{secondaryText}</SSpan>}
    </Container>
  );
}

const Container = styled.button<{
  color: 'primary' | 'secondary';
  disabled?: boolean;
  hidden?: boolean;
  opaque?: boolean;
  width?: string;
  $minWidth?: string;
  $minHeight?: string;
  withoutIconMargin?: boolean;
  $margin?: string;
}>`
  align-items: center;
  border-radius: 16px;
  border: none;
  box-shadow: 0px -4px 3px 0px rgba(0, 0, 0, 0.25) inset;
  background: ${({ color }) => {
    if (color === 'primary')
      return `linear-gradient(180deg, #53D2D7 0%, #27C4CA 100%)`;
    if (color === 'secondary')
      return `linear-gradient(180deg, #EA7A49 0%, #EF6226 100%)`;

    return 'transparent';
  }};

  cursor: pointer;
  display: ${({ hidden }) => (hidden ? 'hidden' : 'flex')};

  flex-direction: column;
  min-height: ${({ $minHeight }) => $minHeight ?? '42px'};
  font-family: 'Riffic Free', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  justify-content: center;
  letter-spacing: normal;
  margin: ${({ $margin }) => ($margin ? $margin : '0')};
  opacity: ${({ disabled, hidden }) => {
    if (hidden) return 0;
    if (disabled) return 0.5;

    return 1;
  }};
  padding: 8px 24px;
  pointer-events: ${({ disabled, hidden }) =>
    (disabled ?? hidden) ? 'none' : 'auto'};

  transition: all 0.6s linear;
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
  width: 100%;
  outline: none;
  @media screen and (max-height: 800px) {
    font-size: 1rem;
    padding: 8px 14px;
  }

  @media screen and (max-width: 450px) {
    font-size: 1rem;
    padding: 8px 14px;
  }
  @media screen and (max-height: 450px) {
    margin: 5px;
  }

  &:focus {
    background: ${({ color }) => {
      if (color === 'primary') return `${classic.token?.colorPrimaryBgHover}`;
      if (color === 'secondary') return `#EF6226`;

      return 'transparent';
    }};
    outline: none;
  }

  &:hover {
    background: ${({ color }) => {
      if (color === 'primary') return `${classic.token?.colorPrimaryBgHover}`;
      if (color === 'secondary') return `#EF6226`;

      return 'transparent';
    }};
  }
`;

const SSpanTitle = styled.span<{
  $textColor?: string | 'white';
  textTransform?: string;
}>`
  color: ${({ $textColor }) =>
    $textColor ? $textColor : `${classic.token?.colorWhite}`};
  text-transform: ${({ textTransform }) => textTransform ?? 'uppercase'};

  @media screen and (max-height: 800px) {
    font-size: 1rem;
  }

  @media screen and (max-width: 450px) {
    font-size: 1rem;
  }
  @media screen and (max-height: 450px) {
    margin: 5px;
  }
`;
const SSpan = styled.span<{ $color?: string }>`
  color: ${({ $color }) =>
    $color ? $color : `${classic.token?.colorTextSecondary}`};
  font-family: 'Riffic Free', sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
  margin-top: 4px;

  @media screen and (max-height: 800px) {
    font-size: 1.05rem;
    margin-top: 2px;
  }

  @media screen and (max-width: 450px) {
    font-size: 1.05rem;
    margin-top: 2px;
  }
`;
